import { Button, Form, Input } from 'antd';
import { Rule } from 'antd/es/form';
import React, { useEffect, useRef } from 'react';

import { Text, useTheme } from '@desygner/ui-common-components';

import { CONSTANTS } from '../../AuthModal.data';
import {
  StyledExtraActionsWrapper,
  StyledModalSubtitle,
  getPrimaryButtonStyle,
  styleLinkButton,
} from '../../AuthModal.styles';
import { AuthViewPropsT, SignupCodeViewLabelsT } from '../../AuthModal.types';
import { focusInput, getServerErrorMessage, handleEnter } from '../../AuthModal.utils';
import { StepHeader } from '../../components/StepHeader/StepHeader';
import { useAuthContext } from '../../context/AuthContext';

export const SignupCodeView = ({ ...props }: AuthViewPropsT) => {
  const { ...labels } = props.labels as SignupCodeViewLabelsT;
  const form = Form.useFormInstance();
  const email = form.getFieldValue(CONSTANTS.form.emailFieldName);
  const codeInputRef = useRef<any>(null);
  const {
    isLoading,
    setIsLoading,
    isLoadingResend,
    setServerError,
    handleSendValidationCode,
    apiValidate,
  } = useAuthContext();

  const rules: Rule[] = [
    {
      required: true,
      message: labels.errors.client.required,
    },
  ];

  const handleValidate = async () => {
    if (isLoading) return;

    await form
      .validateFields()
      .then(async () => {
        setIsLoading?.(true);
        const code = form.getFieldValue(CONSTANTS.form.codeFieldName);

        await apiValidate?.(email, code)
          .then((response) => {
            props.onChangeView?.('signupPassword');
          })
          .catch((err) => {
            const { status } = err.response;

            const serverErrorMessage = getServerErrorMessage(err);

            // Bad code - show error under the input field
            if (status === 400) {
              form.setFields([
                {
                  name: CONSTANTS.form.codeFieldName,
                  errors: [serverErrorMessage],
                },
              ]);
            } else {
              // any other error - show error on top
              setServerError?.(serverErrorMessage);
            }
          })
          .finally(() => {
            setIsLoading?.(false);
          });
      })
      .catch((error: any) => {
        console.error('Form validation failed.', error);
        return;
      })
      .finally(() => {
        setIsLoading?.(false);
      });
  };

  useEffect(() => {
    focusInput(codeInputRef);
  }, []);

  const theme = useTheme();

  return (
    <>
      <StepHeader title={props.labels.title} onBack={() => props.onChangeView?.('loginEmail')} />

      <StyledModalSubtitle>
        <Text size="Body">{labels.getSubtitle(email)}</Text>
      </StyledModalSubtitle>

      <Form.Item name={CONSTANTS.form.codeFieldName} rules={[...rules]}>
        <Input
          placeholder={labels.form.placeholder}
          ref={codeInputRef}
          onKeyDown={(event) => handleEnter(event, handleValidate)}
          onChange={(event) => {
            form.setFieldValue(CONSTANTS.form.codeFieldName, event.target.value);
          }}
        />
      </Form.Item>

      <Button
        type="primary"
        loading={isLoading}
        onClick={handleValidate}
        data-action="auth-signup-validate-email"
        style={getPrimaryButtonStyle(theme)}
      >
        {labels.cta.continue}
      </Button>

      <StyledExtraActionsWrapper>
        <Text size="Body">{labels.didntGetCode}</Text>
        <Button
          type="link"
          style={styleLinkButton}
          loading={isLoadingResend}
          data-action="auth-signup-validate-email-resend-code"
          onClick={() => handleSendValidationCode?.(email, props.onChangeView, labels.errors.e500)}
        >
          {labels.cta.resend}
        </Button>
      </StyledExtraActionsWrapper>
    </>
  );
};
